import {
	DeleteOutlined,
	EditOutlined,
	EnvironmentOutlined,
	LoadingOutlined,
	MessageOutlined,
	PhoneOutlined,
	PlusOutlined,
} from "@ant-design/icons";
import { getClientEnv } from "@shared/env";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Button, Image, message, Popconfirm, Upload } from "antd";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { filesApi } from "@/modules/objects/api/files.api";
import { submitClientSave } from "../services/clients.backend";
import type { ClientDetail } from "../types/clients.types";

interface ClientPersonalCardProps {
	client: ClientDetail;
	isDeleting?: boolean;
	onActiveTabChange?: (tabKey: string) => void;
	onDelete?: () => void;
}

export function formatClientPhone(phone: string) {
	return phone.trim().startsWith("+") ? phone.trim() : `+998 ${phone.trim()}`;
}

export function formatClientAddress(
	region?: null | string,
	district?: null | string,
	address?: null | string
) {
	return [region, district, address].filter((part) => part && part.trim().length > 0).join(", ");
}

export function ClientPersonalCard({
	client,
	isDeleting = false,
	onActiveTabChange,
	onDelete,
}: ClientPersonalCardProps) {
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const env = getClientEnv();
	const [uploadingFront, setUploadingFront] = useState(false);
	const [uploadingBack, setUploadingBack] = useState(false);

	const updateClientMutation = useMutation({
		mutationFn: (updatedPassport: Partial<typeof client.passport>) => {
			return submitClientSave({
				id: client.id,
				firstName: client.firstName,
				lastName: client.lastName,
				patronymic: client.patronymic,
				birthDate: client.birthDate,
				gender: client.gender,
				phone: client.phone,
				phoneAdd1: client.phoneAdd1,
				phoneAdd2: client.phoneAdd2,
				region: client.region,
				district: client.district,
				address: client.address,
				passport: {
					seriesNumber: client.passport.seriesNumber,
					jshshir: client.passport.jshshir,
					issueDate: client.passport.issueDate,
					issuedBy: client.passport.issuedBy,
					passportFrontImage: client.passport.passportFrontImage,
					passportBackImage: client.passport.passportBackImage,
					...updatedPassport,
				},
			});
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["client", client.id] });
			queryClient.invalidateQueries({ queryKey: ["clients"] });
			queryClient.invalidateQueries({ queryKey: ["sales"] });
			queryClient.invalidateQueries({ queryKey: ["payment-schedules"] });
			queryClient.invalidateQueries({ queryKey: ["payments"] });
		},
		onError: () => {
			message.error("Ma'lumotni saqlashda xatolik yuz berdi");
		},
	});

	const getPassportImageUrl = (image?: string) => {
		if (!image) {
			return undefined;
		}
		if (image.startsWith("http://") || image.startsWith("https://")) {
			return image;
		}
		if (image.startsWith("/uploads/")) {
			return `${env.VITE_API_URL}${image}`;
		}
		return `${env.VITE_API_URL}/uploads/${image}`;
	};

	const handleUpload = async (file: File, side: "front" | "back") => {
		if (side === "front") {
			setUploadingFront(true);
		} else {
			setUploadingBack(true);
		}
		try {
			const res = await filesApi.uploadFile(file, "image");
			if (res.url) {
				const field = side === "front" ? "passportFrontImage" : "passportBackImage";
				await updateClientMutation.mutateAsync({
					[field]: res.url,
				});
				message.success("Pasport nusxasi yuklandi!");
			} else {
				message.error("Rasmni yuklashda xatolik yuz berdi");
			}
		} catch (_err) {
			message.error("Rasmni yuklashda xatolik yuz berdi");
		} finally {
			if (side === "front") {
				setUploadingFront(false);
			} else {
				setUploadingBack(false);
			}
		}
		return false;
	};

	const handleRemove = async (e: React.MouseEvent, side: "front" | "back") => {
		e.stopPropagation();
		const field = side === "front" ? "passportFrontImage" : "passportBackImage";
		try {
			await updateClientMutation.mutateAsync({
				[field]: "",
			});
			message.success("Pasport nusxasi o'chirildi!");
		} catch (_err) {
			message.error("O'chirishda xatolik yuz berdi");
		}
	};

	const renderInfoRow = (label: string, value: string) => (
		<div className="crm-client-info-row">
			<span className="crm-user-role">{label}</span>
			<span className="font-bold">{value || "-"}</span>
		</div>
	);

	return (
		<div className="crm-card crm-client-personal-card">
			<div className="crm-card-body crm-client-personal-body">
				<div className="crm-client-personal-head">
					<span className={`crm-mini-avatar crm-client-personal-avatar ${client.tone || "blue"}`}>
						{client.initials}
					</span>
					<div className="crm-client-personal-meta">
						<div className="font-bold crm-client-personal-name">
							{client.lastName} {client.firstName}
						</div>
						<div className="crm-user-role crm-client-personal-subtitle">
							{client.patronymic || ""} - {client.gender === "erkak" ? "erkak" : "ayol"} -{" "}
							{client.birthYear || "-"}-yil
						</div>
					</div>
					<div className="crm-client-personal-top-actions">
						<Button
							icon={<MessageOutlined />}
							onClick={() => {
								if (onActiveTabChange) {
									onActiveTabChange("sms");
								}
							}}
						/>
						{onDelete ? (
							<Popconfirm
								cancelText="Bekor qilish"
								description="Mijoz soft delete orqali o'chiriladi."
								okButtonProps={{ danger: true, loading: isDeleting }}
								okText="O'chirish"
								onConfirm={onDelete}
								title="Mijozni o'chirish?"
							>
								<Button danger icon={<DeleteOutlined />} loading={isDeleting} />
							</Popconfirm>
						) : null}
					</div>
				</div>

				<div>
					<span
						className={`crm-badge ${
							client.status === "faol" || client.status === "active" ? "green" : "red"
						}`}
					>
						{client.status === "faol" || client.status === "active" ? "Faol" : "Bekor qilingan"}
					</span>
				</div>

				<div className="crm-divider crm-client-personal-divider" />

				<div className="crm-client-contact-list">
					<div className="crm-client-contact-item">
						<PhoneOutlined className="crm-user-role" />
						<span>{formatClientPhone(client.phone)}</span>
					</div>
					{client.phoneAdd1 && (
						<div className="crm-client-contact-item">
							<PhoneOutlined className="crm-user-role" />
							<span>
								{formatClientPhone(client.phoneAdd1)}{" "}
								<span className="crm-user-role">(qo'shimcha)</span>
							</span>
						</div>
					)}
					{client.phoneAdd2 && (
						<div className="crm-client-contact-item">
							<PhoneOutlined className="crm-user-role" />
							<span>
								{formatClientPhone(client.phoneAdd2)}{" "}
								<span className="crm-user-role">(qo'shimcha 2)</span>
							</span>
						</div>
					)}
					<div className="crm-client-contact-item crm-client-address-item">
						<EnvironmentOutlined className="crm-user-role" />
						<span>
							{formatClientAddress(client.region, client.district, client.address) || "-"}
						</span>
					</div>
				</div>

				<div className="crm-divider crm-client-personal-divider" />

				<div className="crm-client-passport">
					<h3 className="font-bold crm-client-passport-title">Pasport ma'lumotlari</h3>
					{renderInfoRow("Seriya / Raqam", client.passport.seriesNumber)}
					{renderInfoRow("JShShIR", client.passport.jshshir)}
					{renderInfoRow("Berilgan sana", client.passport.issueDate)}
					{renderInfoRow("Kim tomondan", client.passport.issuedBy)}
				</div>

				<div>
					<div className="crm-user-role crm-client-passport-label">Pasport nusxalari</div>
					<div className="crm-client-passport-grid">
						<div className="crm-client-passport-cell" style={{ flex: "0 0 96px" }}>
							{uploadingFront ? (
								<div className="crm-square-upload-btn">
									<LoadingOutlined />
								</div>
							) : client.passport.passportFrontImage ? (
								<div className="crm-square-upload-preview">
									<Image
										src={getPassportImageUrl(client.passport.passportFrontImage)}
										alt="Old tomon"
										style={{
											width: "96px",
											height: "96px",
											objectFit: "cover",
											borderRadius: "12px",
										}}
										wrapperStyle={{ width: "96px", height: "96px" }}
									/>
									<Button
										type="text"
										icon={<DeleteOutlined style={{ color: "var(--crm-red)" }} />}
										onClick={(e) => {
											handleRemove(e, "front");
										}}
										className="crm-square-upload-delete"
									/>
								</div>
							) : (
								<Upload
									accept="image/*"
									beforeUpload={(file) => {
										handleUpload(file, "front");
										return false;
									}}
									showUploadList={false}
								>
									<div className="crm-square-upload-btn">
										<PlusOutlined style={{ fontSize: "18px", color: "var(--crm-ink-3)" }} />
										<span
											style={{
												fontSize: "11px",
												fontWeight: 500,
												color: "var(--crm-ink-2)",
												marginTop: "4px",
											}}
										>
											Old tomon
										</span>
									</div>
								</Upload>
							)}
						</div>

						<div className="crm-client-passport-cell" style={{ flex: "0 0 96px" }}>
							{uploadingBack ? (
								<div className="crm-square-upload-btn">
									<LoadingOutlined />
								</div>
							) : client.passport.passportBackImage ? (
								<div className="crm-square-upload-preview">
									<Image
										src={getPassportImageUrl(client.passport.passportBackImage)}
										alt="Orqa tomon"
										style={{
											width: "96px",
											height: "96px",
											objectFit: "cover",
											borderRadius: "12px",
										}}
										wrapperStyle={{ width: "96px", height: "96px" }}
									/>
									<Button
										type="text"
										icon={<DeleteOutlined style={{ color: "var(--crm-red)" }} />}
										onClick={(e) => {
											handleRemove(e, "back");
										}}
										className="crm-square-upload-delete"
									/>
								</div>
							) : (
								<Upload
									accept="image/*"
									beforeUpload={(file) => {
										handleUpload(file, "back");
										return false;
									}}
									showUploadList={false}
								>
									<div className="crm-square-upload-btn">
										<PlusOutlined style={{ fontSize: "18px", color: "var(--crm-ink-3)" }} />
										<span
											style={{
												fontSize: "11px",
												fontWeight: 500,
												color: "var(--crm-ink-2)",
												marginTop: "4px",
											}}
										>
											Orqa tomon
										</span>
									</div>
								</Upload>
							)}
						</div>
					</div>
				</div>

				<div className="crm-client-personal-actions">
					<Button
						className="grow"
						icon={<EditOutlined />}
						onClick={() => navigate(`/clients/${client.id}/edit`)}
						style={{ width: "100%" }}
					>
						Tahrir
					</Button>
				</div>
			</div>
		</div>
	);
}
